// Per-form-type detail views. Renders a submitted requisition's form_data
// as a set of read-only sections that mirror each form's layout.
import { useEffect, useMemo, useState } from "react";
import { Paperclip } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/dateFmt";
import { computeGrandTotal } from "@/components/FormShell";
// Phase 25a — smart renderer: never dump JSON blobs to the user.
import { renderFieldValue } from "@/components/FieldValueRenderer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Phase 13.2 — Auto-format ISO-shaped date strings to DD/MM/YYYY on display.
// Recognises "YYYY-MM-DD" (form date input value) and full ISO timestamps.
const _ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
function _autoFmtDate(v) {
  if (typeof v !== "string") return v;
  if (!_ISO_DATE_RE.test(v)) return v;
  return formatDate(v, { includeTime: v.length > 10 });
}

export function FormDetails({ req }) {
  const t = req.form_type;
  if (t === "material") return <MaterialDetails req={req} />;
  if (t === "event") return <EventDetails req={req} />;
  if (t === "guest") return <GuestDetails req={req} />;
  if (t === "transportation") return <TransportationDetails req={req} />;
  if (t === "fnb") return <FnBDetails req={req} />;
  if (t === "travel") return <TravelDetails req={req} />;
  if (t === "hotel") return <HotelDetails req={req} />;
  if (t === "maini") return <MainiDetails req={req} />;
  return <GenericDetails req={req} />;
}

export const FORM_TYPE_LABEL = {
  material: "Material",
  service: "Service",
  event: "Event",
  transportation: "Transportation",
  fnb: "Cake / Food & Beverages",
  travel: "Travel Tickets",
  hotel: "Hotel Booking",
  guest: "Guest Visit - Stay in Staff Quarters",
  advance_requisition: "Advance Requisition",
  pocket_money: "Pocket Money",
  advance_settlement: "Advance Settlement",
  reimbursement: "Reimbursement",
  maini: "Internal Maini Booking",
  print_job: "Print Job Requisition",
};

/* -------- Small primitives -------- */
function Card({ title, children }) {
  return (
    <div className="sheet p-5 sm:p-7">
      <h2 className="font-serif text-xl font-semibold text-[#7a1f2b] mb-4">{title}</h2>
      {children}
    </div>
  );
}
function Cell({ label, value }) {
  const v = Array.isArray(value)
    ? value.map(_autoFmtDate).join(", ")
    : _autoFmtDate(value);
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">{label}</div>
      <div className="text-[#1f2320] mt-0.5 break-words">
        {v === undefined || v === null || v === "" ? "—" : String(v)}
      </div>
    </div>
  );
}
function Grid({ cols = 2, children }) {
  return <dl className={`grid sm:grid-cols-${cols} gap-4 text-sm`}>{children}</dl>;
}
function Block({ label, children }) {
  return (
    <div className="mt-3">
      <div className="text-xs uppercase tracking-widest text-[#2d6b6b] font-semibold mb-1">{label}</div>
      <div className="bg-[#faf5eb] border border-[#e2d7c3] rounded-md p-3 whitespace-pre-wrap text-sm">
        {children || "—"}
      </div>
    </div>
  );
}

/* -------- Attachments (shared) -------- */
const ATTACHMENT_KIND_BADGE = {
  transport_quote:  { label: "Transport Quote",  cls: "bg-[#e8f2f2] text-[#2d6b6b] border-[#2d6b6b]/40" },
  service_quote:    { label: "Service Quote",    cls: "bg-[#efe5f8] text-[#6b3fa0] border-[#6b3fa0]/40" },
  food_menu:        { label: "Food Menu",        cls: "bg-[#faeadd] text-[#a04f22] border-[#a04f22]/40" },
  groceries_list:   { label: "Groceries List",   cls: "bg-[#faeadd] text-[#a04f22] border-[#a04f22]/40" },
  fnb_po_attachment:{ label: "F&B PO Doc",       cls: "bg-[#faeadd] text-[#a04f22] border-[#a04f22]/40" },
  admin1_doc:       { label: "Admin-1 Document", cls: "bg-[#e8f2f2] text-[#2d6b6b] border-[#2d6b6b]/40" },
  bill_attachment:  { label: "Bill",             cls: "bg-[#f9e8ec] text-[#7a1f2b] border-[#7a1f2b]/40" },
  fulfilment_bill:  { label: "Fulfilment Bill",  cls: "bg-[#f9e8ec] text-[#7a1f2b] border-[#7a1f2b]/40" },
  applicant_upload: { label: "Applicant Upload", cls: "bg-[#faf5eb] text-[#b48a45] border-[#b48a45]/40" },
  // Phase 10 — Stores PO quotes & Amey's final bills carry sequential numbering.
  procurement_quote:{ label: "Procurement Quote", cls: "bg-[#e8f2f2] text-[#2d6b6b] border-[#2d6b6b]/40" },
  final_bill:       { label: "Final Bill",        cls: "bg-[#f9e8ec] text-[#7a1f2b] border-[#7a1f2b]/40" },
};
// Phase 10 — kinds that should show a sequential index (1/2/3) after the label.
const SEQUENTIAL_KINDS = new Set(["procurement_quote", "final_bill"]);

export function AttachmentsBlock({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  // Phase 10 — compute sequential index for procurement_quote / final_bill.
  const kindCounters = {};
  const kindIndexByOrder = attachments.map((a) => {
    if (!SEQUENTIAL_KINDS.has(a?.kind)) return null;
    kindCounters[a.kind] = (kindCounters[a.kind] || 0) + 1;
    return kindCounters[a.kind];
  });
  return (
    <div className="sheet p-5 sm:p-7">
      <h3 className="font-serif text-base font-semibold text-[#7a1f2b] mb-2">Attachments</h3>
      <ul className="text-sm space-y-2">
        {attachments.map((a, i) => {
          const badge = ATTACHMENT_KIND_BADGE[a.kind];
          const seq = kindIndexByOrder[i];
          const badgeLabel = badge
            ? (seq ? `${badge.label} ${seq}` : badge.label)
            : null;
          return (
            <li key={i} className="flex flex-wrap items-center gap-2" data-testid={`attachment-${i}`}>
              <Paperclip size={14} className="text-[#2d6b6b] shrink-0" />
              <a href={`${BACKEND}${a.url}`} target="_blank" rel="noreferrer"
                 className="link-vgws" data-testid={`attachment-download-${i}`}>
                {a.filename}
              </a>
              {badge && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest font-semibold ${badge.cls}`}
                      data-testid={`attachment-badge-${i}`}>
                  {badgeLabel}
                </span>
              )}
              <span className="text-xs text-[#6e7770]">
                ({Math.round((a.size || 0) / 1024)} KB · {a.field_name})
              </span>
              {a.uploaded_by_name && (
                <span className="text-xs text-[#6e7770] w-full sm:w-auto">
                  · uploaded by <strong>{a.uploaded_by_name}</strong>
                  {a.uploaded_at && (
                    <> · {new Date(a.uploaded_at).toLocaleString()}</>
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* -------- Applicant + department card (shared header) -------- */
export function ApplicantDeptCard({ req }) {
  const fd = req.form_data || {};
  return (
    <Card title="Applicant & department">
      <Grid cols={2}>
        <Cell label="Applicant" value={`${req.applicant_name} · ${req.applicant_email}`} />
        <Cell label="Selected HOD" value={`${req.hod_name || "—"} · ${req.hod_email || ""}`} />
        <Cell label="Department" value={`${fd.mainDept || "—"} / ${fd.subDept || "—"}`} />
        <Cell label="Contact" value={fd.staffContact} />
      </Grid>
    </Card>
  );
}

/* -------- Material -------- */
function MaterialDetails({ req }) {
  return <MaterialDetailsInner req={req} />;
}

function MaterialDetailsInner({ req: initialReq }) {
  const [req, setReq] = useState(initialReq);
  useEffect(() => setReq(initialReq), [initialReq]);
  const { user } = useAuth();
  const fd = req.form_data || {};
  const items = useMemo(() => fd.items || [], [fd.items]);
  const isSuper = user?.role === "super_admin" || user?.is_super_admin === true;
  const isStoresPO = (req.stores_po_ids || []).includes(user?.id);
  const canBulk = isSuper || isStoresPO
    || ["admin1", "admin1_alt", "admin2"].includes(user?.role);
  const stagesAfterAdmin2 = new Set([
    "process_owner_review", "process_owner", "accounts_review", "accounts",
    "fnb_po_fulfilment", "fnb_po_completion", "completed", "rejected",
  ]);
  const bulkLocked = stagesAfterAdmin2.has(req.current_stage) || false;
  const canEditItemStatus = canBulk && !bulkLocked;

  // Phase 16 (items 4-5) — Row selection + bulk apply.
  const [selected, setSelected] = useState({});
  const [bulkStatus, setBulkStatus] = useState("");
  const [busyBulk, setBusyBulk] = useState(false);
  const summary = items.reduce((acc, it) => {
    const s = (it.admin_status || {}).current;
    if (s === "Approved") acc.approved += 1;
    else if (s === "On Hold") acc.hold += 1;
    else if (s === "Rejected") acc.rejected += 1;
    return acc;
  }, { approved: 0, hold: 0, rejected: 0 });

  const grand = useMemo(() => computeGrandTotal(items), [items]);

  const applyBulkStatus = async (mode /* "all" | "selected" */) => {
    if (!bulkStatus) { toast.error("Pick a status."); return; }
    const rowIndexes = mode === "all"
      ? items.map((_, i) => i)
      : Object.keys(selected).filter((k) => selected[k]).map(Number);
    if (!rowIndexes.length) { toast.error("Select at least one row."); return; }
    setBusyBulk(true);
    try {
      const { data } = await api.patch(
        `/requisitions/${req.ref_code}/line-items-status`,
        { bulk_status: bulkStatus, apply_to: rowIndexes },
      );
      // Optimistic: refetch full req.
      const r = await api.get(`/requisitions/${req.ref_code}`);
      setReq(r.data);
      setSelected({});
      toast.success(`Updated ${data.changed?.length || 0} row(s) to ${bulkStatus}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyBulk(false); }
  };

  const updateStatus = async (idx, status) => {
    try {
      await api.patch(
        `/requisitions/${req.ref_code}/line-items-status`,
        { items: [{ row_index: idx, status }] },
      );
      const r = await api.get(`/requisitions/${req.ref_code}`);
      setReq(r.data);
      toast.success(`Saved · ${status}`, { duration: 2000 });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const allSelected = items.length > 0
    && items.every((_, i) => selected[i]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(items.map((_, i) => [i, true])));
  };

  return (
    <>
      <ApplicantDeptCard req={req} />
      <Card title="Material details">
        <Grid cols={2}>
          <Cell label="Required by (class/wing)" value={fd.materialRequiredBy} />
          <Cell label="Category" value={fd.materialCategory} />
          <Cell label="Tentative date" value={fd.requiredByDate} />
          <Cell label="Quality / brand / warranty" value={fd.qualityBrandWarranty} />
        </Grid>
        <Block label="Purpose">{fd.purposeOfUtility}</Block>

        <div className="mt-5 mb-2 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-serif text-base font-semibold text-[#7a1f2b]">
            Items ({items.length})
          </h3>
          <div className="flex items-center gap-1 text-xs" data-testid="material-status-summary">
            <span className="badge bg-emerald-100 text-emerald-800 border-emerald-300">{summary.approved} Approved</span>
            <span className="badge bg-amber-100 text-amber-800 border-amber-300">{summary.hold} On Hold</span>
            <span className="badge bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40">{summary.rejected} Rejected</span>
            <span className="text-[#6e7770]">of {items.length}</span>
          </div>
        </div>

        <div className="mb-2 text-xs italic bg-[#fcebe9] text-[#b3261e] border-l-4 border-[#b3261e] px-3 py-2 rounded"
             data-testid="material-admin-status-note">
          <strong>Note:</strong> The "Material Status Update" column is reserved for
          Admin-1 (Bursar / CAO) official use only. Applicants cannot edit this field.
        </div>

        {bulkLocked && canBulk && (
          <div className="mb-2 text-xs bg-[#fcebe9] text-[#b3261e] border-l-4 border-[#b3261e] px-3 py-2 rounded"
               data-testid="material-bulk-locked-note">
            Line-items status is <strong>locked</strong> after HOS approval.
          </div>
        )}

        {canEditItemStatus && (
          <div className="mb-2 flex flex-wrap items-center gap-2 bg-[#faf5eb] border border-[#e2d7c3] rounded px-3 py-2"
               data-testid="material-bulk-actions">
            <span className="text-xs font-semibold text-[#7a1f2b]">Bulk action:</span>
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              className="input-vgws py-1 text-sm w-40"
              data-testid="material-bulk-status-select"
            >
              <option value="">— Select —</option>
              <option value="Approved">All Approved</option>
              <option value="On Hold">All On Hold</option>
              <option value="Rejected">All Rejected</option>
            </select>
            <button type="button"
                    disabled={busyBulk || !bulkStatus}
                    onClick={() => applyBulkStatus("selected")}
                    className="btn-vgws btn-teal !py-1 !px-3 text-xs"
                    data-testid="material-bulk-apply-selected">
              Apply to selected
            </button>
            <button type="button"
                    disabled={busyBulk || !bulkStatus}
                    onClick={() => applyBulkStatus("all")}
                    className="btn-ghost !py-1 !px-3 text-xs"
                    data-testid="material-bulk-apply-all">
              Apply to all
            </button>
          </div>
        )}

        <div className="overflow-x-auto rounded-md border border-[#e2d7c3]">
          <table className="material-table" data-testid="detail-items-table">
            <thead><tr>
              {canEditItemStatus && (
                <th className="w-8">
                  <input type="checkbox"
                         checked={allSelected} onChange={toggleAll}
                         data-testid="material-select-all"
                         aria-label="Select all rows" />
                </th>
              )}
              <th className="w-10">#</th><th>Name</th><th className="w-24">Qty</th>
              <th>Link</th><th className="w-24">Cost (₹)</th>
              <th className="w-48">Material Status Update</th>
            </tr></thead>
            <tbody>
              {items.map((it, i) => {
                const st = it.admin_status || { current: null, history: [] };
                const last = (st.history || []).slice(-1)[0];
                return (
                  <tr key={i} data-testid={`detail-item-row-${i}`}>
                    {canEditItemStatus && (
                      <td className="text-center">
                        <input type="checkbox"
                               checked={!!selected[i]}
                               onChange={(e) => setSelected((s) => ({ ...s, [i]: e.target.checked }))}
                               data-testid={`material-row-select-${i}`}
                               aria-label={`Select row ${i + 1}`} />
                      </td>
                    )}
                    <td className="text-center text-[#7a1f2b] font-semibold">{i + 1}</td>
                    <td>{it.name}</td>
                    <td>{it.quantity}</td>
                    <td className="truncate max-w-[240px]">
                      {it.link ? <a href={it.link} target="_blank" rel="noreferrer" className="link-vgws">{it.link}</a> : "—"}
                    </td>
                    <td>{it.cost}</td>
                    <td>
                      {canEditItemStatus ? (
                        <select
                          value={st.current || ""}
                          onChange={(e) => updateStatus(i, e.target.value)}
                          className="input-vgws py-1 text-sm"
                          data-testid={`detail-admin-status-select-${i}`}
                        >
                          <option value="" disabled>— set —</option>
                          <option>Approved</option>
                          <option>On Hold</option>
                          <option>Rejected</option>
                        </select>
                      ) : (
                        <span className={`badge ${
                          st.current === "Approved" ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                          st.current === "On Hold"  ? "bg-amber-100 text-amber-800 border-amber-300" :
                          st.current === "Rejected" ? "bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40" :
                                                       "bg-[#faf5eb] text-[#6e7770] border-[#e2d7c3]"
                        }`} data-testid={`detail-admin-status-badge-${i}`}>
                          {st.current || "Pending Admin-1"}
                        </span>
                      )}
                      {last && (
                        <div className="text-[10px] text-[#6e7770] mt-1"
                             title={`${(st.history || []).length} edit(s) total`}
                             data-testid={`detail-admin-status-last-${i}`}>
                          Last: {last.actor_name}, {new Date(last.at).toLocaleString()}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {items.length > 0 && (
                <tr className="bg-[#faf5eb] font-semibold border-t-2 border-[#7a1f2b]"
                    data-testid="material-grand-total-row">
                  {canEditItemStatus && <td></td>}
                  <td colSpan={4} className="text-right text-[#7a1f2b]">Grand Total</td>
                  <td className="text-[#7a1f2b]" data-testid="material-grand-total-value">
                    {grand.hasUnparseable ? (
                      <span title="Some line items have non-numeric cost"
                            className="cursor-help">
                        ₹{grand.total.toFixed(2)} <span className="text-[#b3261e]">?</span>
                      </span>
                    ) : (
                      <>₹{grand.total.toFixed(2)}</>
                    )}
                  </td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <ResidualFields req={req} />
      <AttachmentsBlock attachments={req.attachments} />
    </>
  );
}

/* -------- Event -------- */
function EventDetails({ req }) {
  const fd = req.form_data || {};
  return (
    <>
      <ApplicantDeptCard req={req} />
      <Card title="Event details">
        <Grid cols={3}>
          <Cell label="Event name" value={fd.eventName} />
          <Cell label="Date" value={fd.eventDate} />
          <Cell label="Days" value={fd.eventDays} />
          <Cell label="Hours" value={fd.eventHours} />
          <Cell label="Start" value={fd.eventStartTime} />
          <Cell label="End" value={fd.eventEndTime} />
        </Grid>
        <Block label="Coordination team">{fd.coordTeam}</Block>
      </Card>
      <Card title="Venue & logistics">
        <Grid cols={3}>
          <Cell label="Hall needed" value={fd.hallNeeded} />
          <Cell label="Hall type" value={fd.hallType} />
          <Cell label="Backdrop needed" value={fd.backdropNeeded} />
          <Cell label="Backdrop type" value={fd.backdropType} />
          <Cell label="Backdrop style" value={fd.backdropStyle} />
          <Cell label="Backdrop size" value={fd.backdropSize} />
          <Cell label="Colour" value={fd.backdropColor} />
          <Cell label="Photos count" value={fd.photosCount} />
          <Cell label="Photo type" value={fd.photosType} />
          <Cell label="School logo" value={fd.schoolLogo} />
          <Cell label="IB logo" value={fd.ibLogo} />
          <Cell label="Sound needed" value={fd.soundNeeded} />
          <Cell label="IT needed" value={fd.itNeeded} />
          <Cell label="IT requirements" value={fd.itRequirements} />
          <Cell label="Tables needed" value={fd.tablesNeeded} />
          <Cell label="Table count" value={fd.tableCount} />
          <Cell label="Chair count" value={fd.chairCount} />
          <Cell label="Didi needed" value={fd.didiNeeded} />
          <Cell label="Didi count" value={fd.didiCount} />
          <Cell label="Housekeeping" value={fd.hkNeeded} />
          <Cell label="HK count" value={fd.hkCount} />
          <Cell label="Food needed" value={fd.foodNeeded} />
          <Cell label="Food option" value={fd.foodOption === "Other" ? fd.foodOther : fd.foodOption} />
          <Cell label="Vehicle needed" value={fd.vehicleNeeded} />
          <Cell label="Vehicle type" value={fd.vehicleType} />
          <Cell label="Seats" value={fd.seatsRequired} />
          <Cell label="Photographer" value={fd.photographerNeeded} />
          <Cell label="Videographer" value={fd.videographerNeeded} />
          <Cell label="Live stream" value={fd.liveStreamNeeded} />
        </Grid>
        {fd.bannerContent && <Block label="Banner content">{fd.bannerContent}</Block>}
        {fd.soundSpecs && <Block label="Sound specifications">{fd.soundSpecs}</Block>}
        {fd.itOther && <Block label="IT other notes">{fd.itOther}</Block>}
        {fd.additionalNotes && <Block label="Additional notes">{fd.additionalNotes}</Block>}
      </Card>
      <ResidualFields req={req} />
      <AttachmentsBlock attachments={req.attachments} />
    </>
  );
}

/* -------- Transportation -------- */
function TransportationDetails({ req }) {
  const fd = req.form_data || {};
  const students = fd.students || [];
  return (
    <>
      <ApplicantDeptCard req={req} />
      <Card title="Travel details">
        <Grid cols={2}>
          <Cell label="Trip type" value={fd.tripType} />
          <Cell label="Travellers" value={fd.travellerCategories} />
        </Grid>
        <Block label="Purpose">{fd.purposeOfTravel}</Block>
        {fd.tripType === "One Way" && <LegBlock leg={fd.oneWay} label="One-way journey" />}
        {fd.tripType === "Round Trip" && (
          <>
            <LegBlock leg={fd.departure} label="Departure journey" />
            <LegBlock leg={fd.return} label="Return journey" />
          </>
        )}
      </Card>
      {students.length > 0 && (
        <Card title={`Students (${students.length})`}>
          <div className="overflow-x-auto rounded-md border border-[#e2d7c3]">
            <table className="material-table">
              <thead><tr><th>#</th><th>Name</th><th>Grade</th><th>Type</th></tr></thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={i}><td>{i + 1}</td><td>{s.name}</td><td>{s.grade}</td><td>{s.type}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <Card title="Passengers & vehicle">
        <Grid cols={3}>
          <Cell label="Students count" value={fd.studentsCount} />
          <Cell label="Parent contact" value={fd.parentContact} />
          <Cell label="Parent email" value={fd.parentEmail} />
          <Cell label="Parents approved" value={fd.parentsApproved} />
          <Cell label="Group SPOC" value={fd.groupSpocName} />
          <Cell label="Group SPOC email" value={fd.groupSpocEmail} />
          <Cell label="Group SPOC contact" value={fd.groupSpocContact} />
          <Cell label="How many travelling" value={fd.howManyTravelling} />
          <Cell label="How many seaters" value={fd.howManySeaters} />
          <Cell label="Vehicle type" value={fd.vehicleType} />
          <Cell label="Sharing" value={fd.sharing} />
          <Cell label="Big luggage" value={fd.bigLuggage} />
          <Cell label="Small luggage" value={fd.smallLuggage} />
          <Cell label="Carrier vehicle" value={fd.carrierVehicle} />
          <Cell label="Attendant" value={fd.attendantRequired} />
          <Cell label="Attendant consent" value={fd.attendantChargesConsent} />
        </Grid>
      </Card>
      <Card title="Safety & payment">
        <Grid cols={2}>
          <Cell label="Emergency contact" value={fd.emergencyContact} />
          <Cell label="School emergency" value={fd.schoolEmergencyContact} />
          <Cell label="Payment by" value={fd.paymentPaidBy} />
          <Cell label="Payment mode" value={fd.paymentMode} />
        </Grid>
        {fd.medicalConditions && <Block label="Medical conditions">{fd.medicalConditions}</Block>}
        {fd.allergies && <Block label="Allergies">{fd.allergies}</Block>}
      </Card>
      <ResidualFields req={req} />
      <AttachmentsBlock attachments={req.attachments} />
    </>
  );
}

function LegBlock({ leg = {}, label }) {
  return (
    <div className="mt-3">
      <div className="text-xs uppercase tracking-widest text-[#2d6b6b] font-semibold mb-1">{label}</div>
      <div className="grid sm:grid-cols-2 gap-3 bg-[#faf5eb] border border-[#e2d7c3] rounded-md p-3 text-sm">
        <div>
          <div className="font-medium">Pick-up · {leg.pickDate} {leg.pickTime}</div>
          <div>{leg.pickLocation || "—"}</div>
          {leg.pickLink && <a className="link-vgws text-xs" href={leg.pickLink} target="_blank" rel="noreferrer">Map link</a>}
        </div>
        <div>
          <div className="font-medium">Drop · {leg.dropDate} {leg.dropTime}</div>
          <div>{leg.dropLocation || "—"}</div>
          {leg.dropLink && <a className="link-vgws text-xs" href={leg.dropLink} target="_blank" rel="noreferrer">Map link</a>}
        </div>
      </div>
    </div>
  );
}

/* -------- F&B / Cake -------- */
function FnBDetails({ req }) {
  const fd = req.form_data || {};
  const isCake = (fd.requirement_type || "").toLowerCase() === "cake";
  if (isCake) return <CakeDetails req={req} />;
  return (
    <>
      <ApplicantDeptCard req={req} />
      <Card title="Requirement (Food & Beverages)">
        <Grid cols={3}>
          <Cell label="People" value={fd.peopleCount} />
          <Cell label="Date" value={fd.requirementDate} />
          <Cell label="Time" value={fd.requirementTime} />
          <Cell label="Service to" value={fd.serviceTo} />
          <Cell label="Items" value={
            (fd.itemsSelected || []).map((x) => (x === "Other" && fd.otherItem) ? `Other: ${fd.otherItem}` : x)
          } />
          <Cell label="Cake required" value={fd.cakeRequired} />
          <Cell label="Cake to" value={fd.cakeTo} />
          <Cell label="Impress consent" value={fd.cakeConsent} />
          <Cell label="Cutleries" value={fd.cutleriesRequired} />
          <Cell label="Serving staff" value={fd.servingStaffRequired} />
        </Grid>
        <Block label="Purpose">{fd.purposeOfRequirement}</Block>
        <Block label="Location">{fd.location}</Block>
        {fd.cookingInstructions && <Block label="Cooking instructions">{fd.cookingInstructions}</Block>}
        {fd.allergies && <Block label="Allergies">{fd.allergies}</Block>}
      </Card>
      <ResidualFields req={req} />
      <AttachmentsBlock attachments={req.attachments} />
    </>
  );
}

/* -------- Cake (Phase 13 — proper cake render) -------- */
function CakeDetails({ req }) {
  const fd = req.form_data || {};
  const isStudent = (fd.cake_to_whom || "").toLowerCase() === "student";
  return (
    <>
      <ApplicantDeptCard req={req} />
      <Card title="Cake Details">
        <Grid cols={3}>
          <Cell label="Cake to whom" value={fd.cake_to_whom} />
          <Cell label="Weight" value={fd.cake_weight} />
          <Cell label="Flavor" value={fd.cake_flavor} />
          <Cell label="Required date" value={fd.cake_required_date} />
          <Cell label="Required time" value={fd.cake_required_time} />
          <Cell label="Cutleries required" value={fd.cutleries_required_cake} />
        </Grid>
        <Block label="Required location">{fd.cake_required_location}</Block>
        {fd.cake_message && <Block label="Message on cake">{fd.cake_message}</Block>}
        {isStudent && (
          <div className="mt-3">
            <div className="text-xs uppercase tracking-widest text-[#2d6b6b] font-semibold mb-1">Student Details</div>
            <Grid cols={4}>
              <Cell label="Student name" value={fd.cake_student_name} />
              <Cell label="Grade" value={fd.cake_student_grade} />
              <Cell label="GR number" value={fd.cake_student_gr_number} />
              <Cell label="Day / Boarding" value={fd.cake_student_day_boarding} />
            </Grid>
          </div>
        )}
      </Card>
      <ResidualFields req={req} />
      <AttachmentsBlock attachments={req.attachments} />
    </>
  );
}

/* -------- Travel -------- */
function TravelDetails({ req }) {
  const fd = req.form_data || {};
  return (
    <>
      <ApplicantDeptCard req={req} />
      <Card title="Travel booking">
        <Grid cols={3}>
          <Cell label="People" value={fd.peopleCount} />
          <Cell label="Booking to" value={fd.travelBookingTo} />
          <Cell label="Vehicle" value={fd.travelVehicle} />
          <Cell label="Country" value={fd.country} />
          <Cell label="State" value={fd.state} />
          <Cell label="City" value={fd.city} />
          <Cell label="Booking date" value={fd.bookingDate} />
          <Cell label="Booking time" value={fd.bookingTime} />
          <Cell label="Vehicle name" value={fd.vehicleNameSpecific} />
          <Cell label="Seating" value={fd.preferredSeating} />
          <Cell label="Departure" value={`${fd.departureDate || ""} ${fd.departureTime || ""}`} />
          <Cell label="Arrival" value={`${fd.arrivalDate || ""} ${fd.arrivalTime || ""}`} />
          <Cell label="Food included" value={fd.includingFood} />
          <Cell label="Cuisine" value={fd.vegOrMulti} />
        </Grid>
        <Block label="Purpose of travel">{fd.purposeOfTravel}</Block>
        {fd.vehicleReason && <Block label="Vehicle reason">{fd.vehicleReason}</Block>}
      </Card>
      {fd.return_travel && Object.values(fd.return_travel).some((v) => String(v || "").trim()) && (
        <Card title="Return Travel Tickets">
          <Grid cols={3}>
            <Cell label="Return date" value={fd.return_travel.returnDate} />
            <Cell label="Return time" value={fd.return_travel.returnTime} />
            <Cell label="Vehicle" value={fd.return_travel.returnVehicle} />
            <Cell label="Preferred seat" value={fd.return_travel.returnSeat} />
            <Cell label="From" value={fd.return_travel.returnCityFrom} />
            <Cell label="To" value={fd.return_travel.returnCityTo} />
          </Grid>
          {fd.return_travel.returnRemarks && (
            <Block label="Additional details">{fd.return_travel.returnRemarks}</Block>
          )}
        </Card>
      )}
      <ResidualFields req={req} />
      <AttachmentsBlock attachments={req.attachments} />
    </>
  );
}

/* -------- Hotel -------- */
function HotelDetails({ req }) {
  const fd = req.form_data || {};
  return (
    <>
      <ApplicantDeptCard req={req} />
      <Card title="Hotel booking">
        <Grid cols={3}>
          <Cell label="People" value={fd.peopleCount} />
          <Cell label="Days" value={fd.daysOfStay} />
          <Cell label="Booking to" value={fd.hotelBookingTo} />
          <Cell label="Check-in" value={`${fd.checkInDate || ""} ${fd.checkInTime || ""}`} />
          <Cell label="Check-out" value={`${fd.checkOutDate || ""} ${fd.checkOutTime || ""}`} />
          <Cell label="Room type" value={fd.room_type} />
          <Cell label="Country" value={fd.country} />
          <Cell label="State" value={fd.state} />
          <Cell label="City" value={fd.city} />
          <Cell label="Hotel name" value={fd.hotelName} />
          <Cell label="Local taxi" value={fd.localTaxi} />
          <Cell label="Food included" value={fd.includingFood} />
          <Cell label="Cuisine" value={fd.vegOrMulti} />
          <Cell label="Meals" value={fd.mealsRequired} />
        </Grid>
        <Block label="Purpose">{fd.purposeOfBooking}</Block>
        <Block label="Reason for hotel">{fd.reasonForHotel}</Block>
        <Block label="Hotel address">{fd.hotelAddress}</Block>
      </Card>
      <ResidualFields req={req} />
      <AttachmentsBlock attachments={req.attachments} />
    </>
  );
}

/* ---- Human labels for form_data keys — used by GenericDetails ---- */
const KEY_LABELS = {
  staffName: "Applicant Name", staffEmail: "Applicant Email",
  staffContact: "Applicant Contact",
  mainDept: "Main Department", subDept: "Sub-department",
  hodApproval: "HOD (Consent)",
  materialRequiredBy: "Required For", materialCategory: "Category",
  purposeOfUtility: "Purpose", requiredByDate: "Required By",
  qualityBrandWarranty: "Quality / Brand / Warranty",
  items: "Materials", services: "Services", bills: "Bills",
  is_emergency: "Emergency", emergency_reason: "Emergency Reason",
  advanceAmount: "Advance Required (₹)", toShri: "To Shri / Smt.",
  purpose: "Purpose", previousAdvanceBalance: "Previous Advance Balance",
  studentName: "Student Name", grade: "Grade", amount: "Amount (₹)", encl: "Encl.",
  personName: "Name of Person", designation: "Designation",
  advanceDrawn: "Advance Drawn (₹)", totalExpenses: "Total Expenses",
  balanceRefunded: "Balance Refunded", excessOverAdvance: "Excess over Advance",
  employeeName: "Employee Name", empCode: "Emp. Code",
  travelApprovedBy: "Travel Approved By",
  purposeOfTravel: "Purpose of Travel",
  daFromDate: "From Date", daToDate: "To Date",
  daNoOfDays: "No. of Days", daAmountCalculated: "Amount Calculated",
  service_approx_price: "Approx Price (Service PO)",
  fnb_approx_price:     "Approx Price (F&B PO)",
  admin1_approx_price:  "Approx Price (Admin-1)",
  transport_approx_price: "Approx Price (Transport PO)",
};

const HIDDEN_KEYS = new Set([
  "attachments",
  "service_approx_price_at", "service_approx_price_by",
  "fnb_approx_price_at",     "fnb_approx_price_by",
  "admin1_approx_price_at",  "admin1_approx_price_by",
  "transport_approx_price_at", "transport_approx_price_by",
  "consents", "consents_accepted_at",
  // Phase 25b — workflow transients consumed by the backend before
  // the record is stored; never surface them to end users.
  "is_test_submission",
  "is_hod_self", "is_admin1_self",
]);

// Phase 25b — Per-form "keys explicitly rendered by the dedicated
// detail renderer". Any form_data key not in this set + not in
// HIDDEN_KEYS gets surfaced automatically via <ResidualFields />.
const COVERED_KEYS_BY_FORM = {
  material: new Set([
    "staffName", "staffEmail", "staffContact",
    "mainDept", "subDept", "hodApproval",
    "materialRequiredBy", "materialCategory", "requiredByDate",
    "qualityBrandWarranty", "purposeOfUtility", "items", "grand_total",
  ]),
  service: new Set([
    "staffName", "staffEmail", "staffContact",
    "mainDept", "subDept", "hodApproval",
    "materialRequiredBy", "materialCategory", "requiredByDate",
    "qualityBrandWarranty", "purposeOfUtility", "services", "grand_total",
  ]),
  event: new Set([
    "staffName", "staffEmail", "staffContact",
    "mainDept", "subDept", "hodApproval",
    "eventName", "eventDate", "eventDays", "eventHours",
    "eventStartTime", "eventEndTime", "eventTime",
    "coordTeam",
    "hallNeeded", "hallType", "location",
    "backdropNeeded", "backdropType", "backdropStyle",
    "backdropSize", "backdropColor",
    "photosCount", "photosType", "schoolLogo", "ibLogo",
    "soundNeeded", "itNeeded", "itRequirements",
    "tablesNeeded", "tableCount", "chairCount",
    "didiNeeded", "didiCount", "hkNeeded", "hkCount",
    "foodNeeded", "foodOption", "foodOther",
    "vehicleNeeded", "vehicleType", "seatsRequired", "expectedAudience",
    "photographerNeeded", "videographerNeeded", "liveStreamNeeded",
    "bannerContent", "soundSpecs", "itOther",
    "additionalNotes", "purposeOfEvent",
  ]),
  transportation: new Set([
    "staffName", "staffEmail", "staffContact",
    "mainDept", "subDept", "hodApproval",
    "tripType", "travellerCategories", "purposeOfTravel",
    "oneWay", "departure", "return",
    "students", "staff", "guests", "others",
    "studentsCount", "parentContact", "parentEmail", "parentsApproved",
    "groupSpocName", "groupSpocEmail", "groupSpocContact",
    "howManyTravelling", "howManySeaters",
    "peopleCount", "vehicleType",
    "sharing", "bigLuggage", "smallLuggage",
    "carrierVehicle", "attendantRequired", "attendantChargesConsent",
    "emergencyContact", "schoolEmergencyContact",
    "paymentPaidBy", "paymentMode",
    "payment_paid_by", "payment_mode",
    "medicalConditions", "allergies",
    "supporting_document",
    // Phase 22b extras — appear only when captured; still "covered"
    // by the dedicated transport renderer + summary extension.
    "po_trip_type_confirmed", "po_amount", "po_remark",
    "accounts_ack_remark", "final_trip_status",
    "final_price_applicable", "final_po_remark",
    "accounts_final_amount_debited", "accounts_final_remark",
  ]),
  fnb: new Set([
    "staffName", "staffEmail", "staffContact",
    "mainDept", "subDept", "hodApproval",
    "requirement_type",
    // FnB (non-cake)
    "peopleCount", "requirementDate", "requirementTime",
    "serviceTo", "itemsSelected", "otherItem",
    "cakeRequired", "cakeTo", "cakeConsent",
    "cutleriesRequired", "servingStaffRequired",
    "purposeOfRequirement", "location",
    "cookingInstructions", "allergies",
    // Cake
    "cake_to_whom", "cake_weight", "cake_flavor",
    "cake_required_date", "cake_required_time",
    "cake_required_location", "cake_message",
    "cutleries_required_cake",
    "cake_student_name", "cake_student_grade",
    "cake_student_gr_number", "cake_student_day_boarding",
  ]),
  travel: new Set([
    "staffName", "staffEmail", "staffContact",
    "mainDept", "subDept", "hodApproval",
    "peopleCount", "travelBookingTo", "travelVehicle",
    "country", "state", "city",
    "bookingDate", "bookingTime",
    "vehicleNameSpecific", "preferredSeating",
    "departureDate", "departureTime",
    "arrivalDate", "arrivalTime",
    "includingFood", "vegOrMulti",
    "purposeOfTravel", "vehicleReason",
    "return_travel",
  ]),
  hotel: new Set([
    "staffName", "staffEmail", "staffContact",
    "mainDept", "subDept", "hodApproval",
    "peopleCount", "daysOfStay", "hotelBookingTo",
    "checkInDate", "checkInTime",
    "checkOutDate", "checkOutTime",
    "room_type", "country", "state", "city",
    "hotelName", "localTaxi",
    "includingFood", "vegOrMulti", "mealsRequired",
    "purposeOfBooking", "reasonForHotel", "hotelAddress",
  ]),
  guest: new Set([
    "staffName", "staffEmail", "staffContact",
    "mainDept", "subDept", "hodApproval",
    "employeeName", "designation", "mobileNo",
    "flatNo", "maritalStatus",
    "fromDate", "toDate", "daysCount",
    "guests", "vehicleType", "vehicleRegNo",
  ]),
  maini: new Set([
    "staffName", "staffEmail", "staffContact",
    "mainDept", "subDept", "hodApproval",
    "utility_purpose", "purpose", "purposeOfBooking",
    "required_date", "bookingDate", "fromDate",
    "required_time", "fromTime", "toTime",
    "hallName", "venue", "peopleCount", "expectedAudience",
  ]),
  // The following forms route through GenericDetails which already
  // iterates every form_data key — residuals included.
};

function humanLabel(k) {
  if (KEY_LABELS[k]) return KEY_LABELS[k];
  return k.replace(/([A-Z])/g, " $1")
          .replace(/^./, (c) => c.toUpperCase())
          .replace(/_/g, " ");
}

function formatScalar(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

/* -------- Residual fields (Phase 25b) --------
 * Surfaces any form_data key the dedicated detail renderer did NOT
 * explicitly place. Guarantees new / operational / resubmitted fields
 * (e.g. admin1_approx_price, re_edit_remark, drive_sync_status) never
 * silently drop off the detail page. */
function ResidualFields({ req }) {
  const fd = req.form_data || {};
  const ft = req.form_type;
  const covered = COVERED_KEYS_BY_FORM[ft];
  // GenericDetails already iterates every form_data key — don't
  // duplicate on those routes. Only forms with a dedicated detail
  // renderer (and therefore an entry in COVERED_KEYS_BY_FORM) go
  // through this safety net.
  if (!covered) return null;
  const residuals = Object.entries(fd).filter(([k, v]) => {
    if (HIDDEN_KEYS.has(k)) return false;
    if (covered.has(k)) return false;
    if (v === null || v === undefined || v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (typeof v === "object" && !Array.isArray(v)
         && Object.keys(v).length === 0) return false;
    return true;
  });
  if (residuals.length === 0) return null;
  return (
    <Card title="Additional details">
      <Grid cols={2}>
        {residuals.map(([k, v]) => (
          <div key={k} data-testid={`residual-field-${k}`}>
            <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
              {humanLabel(k)}
            </div>
            <div className="text-[#1f2320] mt-0.5 break-words">
              {typeof v === "object" && v !== null
                ? renderFieldValue(v, k)
                : formatScalar(v)}
            </div>
          </div>
        ))}
      </Grid>
    </Card>
  );
}

function RowsTable({ rows }) {
  if (!rows || rows.length === 0) return <div className="text-sm text-[#6e7770]">—</div>;
  const cols = Array.from(rows.reduce((acc, r) => {
    Object.keys(r || {}).forEach((k) => acc.add(k));
    return acc;
  }, new Set()));
  return (
    <div className="overflow-x-auto rounded-md border border-[#e2d7c3] bg-white">
      <table className="w-full text-xs">
        <thead className="bg-[#faf5eb] text-[#5a1520]">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left p-1.5 pl-2 font-semibold uppercase tracking-widest">
                {humanLabel(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-[#faf5eb]/40" : ""}>
              {cols.map((c) => (
                <td key={c} className="p-1.5 pl-2 border-t border-[#e2d7c3] align-top">
                  {typeof r[c] === "object" && r[c] !== null
                    ? renderFieldValue(r[c], c)
                    : formatScalar(r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GenericDetails({ req }) {
  const fd = req.form_data || {};
  const entries = Object.entries(fd).filter(([k]) => !HIDDEN_KEYS.has(k));
  const scalarPairs = [];
  const rowSections = [];
  const consents = fd.consents;
  for (const [k, v] of entries) {
    if (Array.isArray(v) && v.length && typeof v[0] === "object") {
      rowSections.push([k, v]);
    } else {
      scalarPairs.push([k, v]);
    }
  }

  return (
    <>
      <ApplicantDeptCard req={req} />
      <Card title="Form details">
        {scalarPairs.length > 0 && (
          <Grid cols={2}>
            {scalarPairs.map(([k, v]) => (
              typeof v === "object" && v !== null ? (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
                    {humanLabel(k)}
                  </div>
                  <div className="text-[#1f2320] mt-0.5 break-words">
                    {renderFieldValue(v, k)}
                  </div>
                </div>
              ) : (
                <Cell key={k} label={humanLabel(k)} value={v} />
              )
            ))}
          </Grid>
        )}
        {consents && typeof consents === "object" && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-widest text-[#2d6b6b] font-semibold mb-1">
              Consents
            </div>
            <ul className="text-sm space-y-0.5">
              {Object.entries(consents).map(([k, v]) => (
                <li key={k} className="flex items-center gap-2">
                  <span className={v ? "text-emerald-700" : "text-[#b3261e]"}>{v ? "✓" : "✗"}</span>
                  <span>{humanLabel(k)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {rowSections.map(([k, rows]) => (
        <Card key={k} title={humanLabel(k) + ` (${rows.length})`}>
          <RowsTable rows={rows} />
        </Card>
      ))}

      <ResidualFields req={req} />
      <AttachmentsBlock attachments={req.attachments} />
    </>
  );
}

/* -------- Guest Visit - Stay in Staff Quarters -------- */
function GuestDetails({ req }) {
  const fd = req.form_data || {};
  const guests = fd.guests || [];
  return (
    <>
      <Card title="Employee / Applicant details">
        <Grid cols={2}>
          <Cell label="Name of Employee" value={fd.employeeName} />
          <Cell label="Designation" value={fd.designation} />
          <Cell label="Main Department" value={fd.mainDept} />
          <Cell label="Sub-department" value={fd.subDept} />
          <Cell label="Flat No." value={fd.flatNo} />
          <Cell label="Marital status" value={fd.maritalStatus} />
          <Cell label="Mobile No." value={fd.mobileNo} />
        </Grid>
      </Card>

      <Card title={`Particulars of Guest(s) (${guests.length})`}>
        <div className="overflow-x-auto rounded-md border border-[#e2d7c3]">
          <table className="material-table" data-testid="guest-detail-guests-table">
            <thead>
              <tr>
                <th className="w-10">Sl.</th>
                <th>Name</th>
                <th className="w-20">Age</th>
                <th>Relationship</th>
                <th>ID Proof</th>
              </tr>
            </thead>
            <tbody>
              {guests.map((g, i) => (
                <tr key={i} data-testid={`guest-detail-row-${i}`}>
                  <td className="text-center text-[#7a1f2b] font-semibold">{i + 1}</td>
                  <td>{g.name}</td>
                  <td>{g.age}</td>
                  <td>{g.relationship}</td>
                  <td className="truncate max-w-[280px]">
                    {g.idProof ? (
                      <a href={`${BACKEND}${g.idProof.url}`} target="_blank" rel="noreferrer"
                         className="link-vgws inline-flex items-center gap-1">
                        <Paperclip size={12} /> {g.idProof.filename}
                      </a>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Vehicle details">
        <Grid cols={2}>
          <Cell label="Vehicle Type" value={fd.vehicleType || "Not Applicable"} />
          <Cell label="Vehicle Registration No." value={fd.vehicleRegNo || "—"} />
        </Grid>
      </Card>

      <Card title="Duration of Stay">
        <Grid cols={3}>
          <Cell label="Permission required for" value={`${fd.daysCount} day(s)`} />
          <Cell label="From" value={fd.fromDate} />
          <Cell label="To" value={fd.toDate} />
        </Grid>
      </Card>

      <Card title="Undertaking">
        <Block label="Statement">
          I undertake that I shall be fully responsible for the conduct of the above-mentioned
          guest(s) on the school campus.
        </Block>
        <Cell label="Applicant agreement" value={fd.undertakingAgreed ? "Agreed" : "Not agreed"} />
      </Card>

      <ResidualFields req={req} />
      <AttachmentsBlock attachments={req.attachments} />
    </>
  );
}


/* -------- Phase 12: Internal Maini Booking -------- */
function MainiDetails({ req }) {
  const fd = req.form_data || {};
  return (
    <>
      <Card title="Requested by">
        <Grid cols={3}>
          <Cell label="Applicant / SPOC" value={fd.staffName} />
          <Cell label="Email" value={fd.staffEmail} />
          <Cell label="Contact" value={fd.staffContact} />
          <Cell label="Main Department" value={fd.mainDept} />
          <Cell label="Sub-department" value={fd.subDept} />
          <Cell label="HOD selected" value={fd.hodApproval || "—"} />
        </Grid>
      </Card>

      <Card title="Requirement details">
        <Grid cols={3}>
          <Cell label="Utility Purpose" value={fd.utility_purpose} />
          <Cell label="Required Date" value={fd.required_date} />
          <Cell label="Required Time" value={fd.required_time} />
        </Grid>
      </Card>

      <ResidualFields req={req} />
      <AttachmentsBlock attachments={req.attachments} />
    </>
  );
}
